import { selectSignerFields, signerFlagsFor, signingOrderFor } from "./selectSignerFields";
import { cpBlockedReason, cpFieldContext, resolveCpFields, stateNameOf } from "./cpFieldRules";

// sign-backend's link generators make secrets as `${template_id}-${ms}-${hex}`.
const SECRET = /^\d+-\d+-[0-9a-f]+$/;
export const MAX_BATCH = 60;
const TYPE_FOR_MODE = { collabPhys: 6, collabOperator: 7 };

// ENG-1865: the portal passes the pending links' secrets after "#", comma-separated.
export function parseSignAllHash(hash) {
  const secrets = [];
  for (const part of String(hash ?? "").replace(/^#/, "").split(",")) {
    const secret = part.trim();
    if (SECRET.test(secret) && !secrets.includes(secret)) secrets.push(secret);
    if (secrets.length === MAX_BATCH) break;
  }
  return secrets;
}

// One link of the wrong type, or of another signer, refuses the whole batch.
export function checkBatch(loaded, mode) {
  const agreements = [];
  const alreadySigned = [];
  const unavailable = [];
  const refuse = (refusal) => ({ refusal, agreements: [], alreadySigned: [], unavailable: [] });
  let signer = null;
  for (const item of loaded) {
    if (!item.signLink) {
      unavailable.push(item);
      continue;
    }
    if (item.signLink.document_type !== TYPE_FOR_MODE[mode]) return refuse("wrong-type");
    const id = mode === "collabPhys" ? item.signLink.collab_phys_id : item.signLink.operator_id;
    // A link without its signer's id can't be shown to be this signer's, so it refuses the batch as a second signer would.
    if (id === null || id === undefined || id === "") return refuse("mixed-signer");
    const key = String(id);
    if (signer === null) signer = key;
    else if (key !== signer) return refuse("mixed-signer");
    (item.signLink.status === 2 ? alreadySigned : agreements).push(item);
  }
  const refusal = agreements.length ? null : alreadySigned.length ? "all-signed" : "nothing-to-sign";
  return { refusal, agreements, alreadySigned, unavailable };
}

// Two agreements in one state are numbered.
export function labelByState(items, stateList) {
  const nameOf = (item) => stateNameOf(item.signLink.state, stateList);
  const totals = {};
  items.forEach((item) => {
    totals[nameOf(item)] = (totals[nameOf(item)] || 0) + 1;
  });
  const seen = {};
  return items.map((item) => {
    const name = nameOf(item);
    if (totals[name] === 1) return { ...item, label: name };
    seen[name] = (seen[name] || 0) + 1;
    return { ...item, label: `${name}, ${seen[name]} of ${totals[name]}` };
  });
}

// The same field scoping and record values the single CP page uses, per agreement.
export function prepareAgreements(eligible, mode, today, stateList) {
  return labelByState(eligible, stateList).map(({ secret, signLink, label }) => {
    const flags = signerFlagsFor(mode, signLink);
    const order = signingOrderFor(flags);
    const scoped = selectSignerFields(signLink.templateField, order, { includeUnordered: order === 1 });
    const context = cpFieldContext(signLink, flags, today, stateList);
    const cpFields = resolveCpFields(scoped, context);
    return {
      secret,
      signLink,
      label,
      signerName: context.name,
      signatureFields: scoped.filter((field) => field?.field_type === "signature"),
      cpFields,
      blocked: cpBlockedReason(cpFields, context),
    };
  });
}

// An agreement whose PDF the pre-flight couldn't build, for a reason the signer can't fix, is held like a blocked one.
export const withHeld = (agreements, held) =>
  agreements.map((agreement) =>
    !agreement.blocked && held[agreement.secret] ? { ...agreement, blocked: "build", heldMessage: held[agreement.secret] } : agreement
  );

// ENG-1826: an agreement the table holds can't be signed, but the rest of the batch still can; held ones are grouped by message.
export function splitBlocked(agreements) {
  const labelsBlocked = (reason) => agreements.filter((agreement) => agreement.blocked === reason).map((agreement) => agreement.label);
  const held = [];
  for (const agreement of agreements.filter((item) => item.blocked === "build")) {
    const group = held.find((item) => item.message === agreement.heldMessage);
    if (group) group.labels.push(agreement.label);
    else held.push({ message: agreement.heldMessage, labels: [agreement.label] });
  }
  return {
    signable: agreements.filter((agreement) => !agreement.blocked),
    blockedMissing: labelsBlocked("missing"),
    blockedUnavailable: labelsBlocked("unavailable"),
    blockedDetails: labelsBlocked("details-unavailable"),
    held,
  };
}
